import React, { useMemo } from 'react'
import { createTheme, MuiThemeProvider } from '@material-ui/core/styles'

const defaultColors = {
  primary: {
    main: '#4285F4'
  },
  secondary: {
    main: '#262f3d'
  }
}

const useFonts = (appData, fonts, hasSize) => {
  return (
    fonts
      .filter((font) => font.key === appData?.theme?.fontFamily)
      .map((font, index) => (font[index] = `${hasSize ? font.size : font.label}`))
      .join('') || undefined
  )
}

const defaultFonts = [
  { key: 'arial', label: 'Arial, sans-serif' },
  { key: 'alegreya', label: 'Alegreya, serif' },
  { key: 'stix_two_text', label: 'STIX Two Text, serif' },
  { key: 'pt_sans', label: 'PT Sans, sans-serif' },
  { key: 'roboto', label: 'Roboto, sans-serif' },
  { key: 'roboto_condensed', label: 'Roboto Condensed, sans-serif' },
  { key: 'external', label: 'Font External' }
]

const defaultButton = {
  fontSize: 14,
  borderRadius: 4,
  height: 44
}

const createMyTheme = (appData = {}) => {
  const fontTheme = useFonts(appData, defaultFonts)
  const isFontExternal = fontTheme === 'Font External'
  let external = null

  if (isFontExternal) {
    external = {
      fontFamily: 'Font External',
      src: `url(${appData?.theme?.fontExternal}) format('woff2')`
    }
  }

  const customTheme = createTheme({
    typography: {
      useNextVariants: true,
      fontFamily: fontTheme ?? 'Roboto, Regular',
      fontSize: 16
    },
    palette: {
      type: 'light',
      ...(appData.colorPrimary && appData.colorSecondary && !appData.theme
        ? {
            primary: {
              main: appData.colorPrimary
            },
            secondary: {
              main: appData.colorPrimary
            }
          }
        : appData.theme
        ? {
            primary: {
              main: appData?.theme?.colorAction
            },
            secondary: {
              main: appData?.theme?.colorSearch
            }
          }
        : appData.colors
        ? appData.colors
        : defaultColors)
    },
    shape: {
      borderRadius: 4
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 930,
        lg: 1280,
        xl: 1920
      }
    },
    lineLimit: (n = 1) => ({
      display: '-webkit-box',
      '-webkit-line-clamp': n,
      '-webkit-box-orient': 'vertical',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }),
    overrides: {
      MuiTooltip: {
        tooltip: {
          fontSize: '0.9rem',
          backgroundColor: 'rgba(0, 0, 0, .9)'
        }
      },
      MuiFormLabel: {
        root: {
          color: 'rgba(0, 0, 0, .87)',
          '&.Mui-focused, &.MuiFormLabel-filled, &.MuiInputLabel-outlined:not(&[data-shrink=false]), &.MuiInputLabel-shrink':
            {
              fontWeight: 'bold'
            }
        }
      },
      MuiCssBaseline: {
        '@global': {
          '@font-face': isFontExternal ? [external] : ''
        }
      },
      MuiButton: {
        // gofind button standard
        contained: defaultButton,
        text: defaultButton,
        outlined: defaultButton
      }
    },
    gofind: {
      desktopMaxWidth: 930,
      asideWidth: 586,
      scrollBar: {
        '&::-webkit-scrollbar': { width: 6, borderRadius: 6 },
        '&::-webkit-scrollbar-track': { backgroundColor: '#EBEBEB', borderRadius: 6 },
        '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0, 0, 0, .40)', borderRadius: 6 }
      }
    }
  })

  return customTheme
}

export default function Theme({ children, config }) {
  const theme = useMemo(() => {
    const result = createMyTheme(config)
    window.theme = result

    return result
  }, [config])

  return <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
}
